import { apiClient } from "../apiClient";
import { CHANNELS } from "@common";
import type {
  FacebookPage,
  FacebookPagesResponse,
  MessageResponse,
} from "@interfaces/api.interface";
import type { Message, Conversation } from "@interfaces/chat.interface";

export type { FacebookPage };

const chunkArray = <T>(array: T[], chunkSize: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

const MAX_EXTERNAL_IDS_PER_REQUEST = 100;

export const getFacebookPages = async (): Promise<FacebookPage[]> => {
  const response = await apiClient.get<FacebookPagesResponse>(
    "/api/facebook/internal/pages"
  );
  return response.data.items;
};

export interface GetFacebookMessagesParams {
  conversationId: string;
  pageId: string;
  offset?: number;
  limit?: number;
  signal?: AbortSignal;
}

// Reuse common MessageResponse interface
export type FacebookMessageResponse = MessageResponse;

export interface FacebookMessagesApiResponse {
  code: number;
  data: {
    items: FacebookMessageResponse[];
    limit: number;
    offset: number;
    total: number;
  };
  message: string;
}

const transformFacebookMessage = (
  fbMessage: FacebookMessageResponse
): Message => {
  const createdAt = new Date(fbMessage.timestampMs).toISOString();

  const messageTypeMap: Record<string, Message["type"]> = {
    TEXT: "text",
    IMAGE: "image",
    VIDEO: "video",
    FILE: "file",
    LINK: "link",
    AUDIO: "audio",
  };

  const type = messageTypeMap[fbMessage.messageType] || "text";

  // Handle both "OUTBOUND" (Facebook) and "OUTGOING" (Zalo) directions
  const isOutbound =
    fbMessage.direction === "OUTBOUND" ||
    fbMessage.direction === "OUTGOING";
  const status: Message["status"] = isOutbound ? "sent" : "read";

  // Extract attachments from fbMessage.attachments or from metadata
  const attachments =
    fbMessage.attachments ||
    (fbMessage.metadata?.attachments as Array<{
      type: string;
      payload?: { url?: string };
      url?: string;
    }> | undefined);

  return {
    id: fbMessage.messageId || "",
    conversationId: fbMessage.conversationId,
    sender: {
      id: fbMessage.senderId,
      name: fbMessage.senderName,
      avatar: fbMessage.senderAvatar,
    },
    body: fbMessage.content,
    createdAt,
    status,
    type,
    meta: {
      receiverName: fbMessage.receiverName,
      receiverAvatar: fbMessage.receiverAvatar,
      receiverId: fbMessage.receiverId,
      direction: isOutbound ? "OUTBOUND" : "INBOUND",
      conversationName: fbMessage.conversationName,
      conversationAvatarUrl: fbMessage.conversationAvatarUrl,
      ...(fbMessage.actorSource && { actorSource: fbMessage.actorSource }),
      ...(attachments && { attachments }),
    },
  };
};

export interface GetFacebookConversationsResult {
  conversations: Conversation[];
  offset: number;
  total: number;
  limit: number;
}

export const getFacebookConversations = async (params: {
  pageId: string;
  offset?: number;
  limit?: number;
}): Promise<Conversation[]> => {
  const result = await getFacebookConversationsWithMeta(params);
  return result.conversations;
};

export const getFacebookConversationsWithMeta = async (params: {
  pageId: string;
  offset?: number;
  limit?: number;
  tagIds?: string[];
  search?: string;
  folder?: string;
  excludeFolders?: string[];
  signal?: AbortSignal;
}): Promise<GetFacebookConversationsResult> => {
  const {
    pageId,
    offset = 0,
    limit = 50,
    tagIds,
    search,
    folder,
    excludeFolders,
    signal,
  } = params;

  try {
    const requestParams: Record<string, unknown> = {
      pageId,
      offset,
      limit,
    };

    if (tagIds && tagIds.length > 0) {
      requestParams.tagIds = tagIds.join(",");
    }

    if (search && search.trim()) {
      requestParams.search = search.trim();
    }

    if (folder) {
      requestParams.folder = folder;
    }

    if (excludeFolders && excludeFolders.length > 0) {
      requestParams.excludeFolders = excludeFolders.join(",");
    }

    const response = await apiClient.get<FacebookMessagesApiResponse>(
      `/api/facebook/internal/conversations/messages`,
      {
        params: requestParams,
        signal,
      }
    );

    const messages = response.data?.items || [];

    if (!Array.isArray(messages)) {
      console.error("Messages is not an array:", messages);
      return {
        conversations: [],
        offset: response.data?.offset || 0,
        total: response.data?.total || 0,
        limit: response.data?.limit || 50,
      };
    }

    const conversations: Conversation[] = [];
    const conversationMap = new Map<string, FacebookMessageResponse>();

    messages.forEach((msg) => {
      const existing = conversationMap.get(msg.conversationId);
      if (!existing || msg.timestampMs > existing.timestampMs) {
        conversationMap.set(msg.conversationId, msg);
      }
    });

    conversationMap.forEach((lastMessage, convId) => {
      const transformedMessage = transformFacebookMessage(lastMessage);
      const conversationName: string =
        (transformedMessage.meta?.conversationName as string | undefined) ||
        (transformedMessage.meta?.receiverName as string | undefined) ||
        transformedMessage.sender.name ||
        "Unknown";
      const receiverAvatar: string = transformedMessage.meta?.conversationAvatarUrl || "";
      // Get isPinned from the last message (most recent message) of the conversation
      const isPinned = lastMessage.isPinned ?? false;

      const conversation: Conversation = {
        id: convId,
        channel: CHANNELS.FACEBOOK,
        name: conversationName,
        fanpageName: undefined,
        avatar: receiverAvatar,
        isGroup: false,
        members: [],
        lastMessage: transformedMessage,
        unreadCount: 0,
        isPinned: isPinned,
        isMuted: false,
        typingUsers: [],
      };

      conversations.push(conversation);
    });

    return {
      conversations,
      offset: response.data?.offset || 0,
      total: response.data?.total || 0,
      limit: response.data?.limit || 50,
    };
  } catch (error) {
    console.error("Error in getFacebookConversationsWithMeta:", error);
    throw error;
  }
};

export const getFacebookMessagesDetail = async (
  params: GetFacebookMessagesParams
): Promise<Message[]> => {
  const result = await getFacebookMessagesDetailWithMeta(params);
  return result.messages;
};

export interface GetFacebookMessagesDetailResult {
  messages: Message[];
  offset: number;
  total: number;
  limit: number;
}

export interface UploadAttachmentParams {
  file: File;
  pageId: string;
}

export interface UploadAttachmentResponse {
  code: number;
  message: string;
  data?: {
    graphResponse?: {
      attachment_id: string;
    };
    attachmentId: string;
    pageId: string;
  };
}

export const uploadFacebookAttachment = async (
  params: UploadAttachmentParams
): Promise<UploadAttachmentResponse> => {
  const { file, pageId } = params;

  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("pageId", pageId);

    const response = await apiClient.post<UploadAttachmentResponse>(
      "/api/facebook/messenger/attachments/multipart",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response;
  } catch (error) {
    console.error("Error uploading attachment:", error);
    throw error;
  }
};

export interface SendFacebookMessageParams {
  pageId: string;
  recipientId: string;
  text?: string;
  attachmentId?: string;
  attachmentType?: "image" | "video" | "file";
}

export interface SendFacebookMessageResponse {
  code: number;
  message: string;
  data?: {
    messageId?: string;
    [key: string]: unknown;
  };
}

export const sendFacebookMessage = async (
  params: SendFacebookMessageParams
): Promise<SendFacebookMessageResponse> => {
  const { pageId, recipientId, text, attachmentId, attachmentType } = params;

  try {
    const messagePayload: Record<string, unknown> = {};

    if (attachmentId && attachmentType) {
      messagePayload.attachment = {
        payload: {
          attachment_id: attachmentId,
        },
        type: attachmentType,
      };
    } else if (text) {
      messagePayload.text = text;
    }

    const response = await apiClient.post<SendFacebookMessageResponse>(
      "/api/facebook/messenger/messages",
      {
        message: messagePayload,
        messagingType: "RESPONSE",
        pageId: pageId,
        recipient: {
          id: recipientId,
        },
      }
    );

    if (!response) {
      return {
        code: 200,
        message: "Message sent successfully",
        data: {},
      };
    }

    if (response.code === -1001) {
      console.log("Facebook API response:", response);
      console.error("Facebook API error -1001:", response.message);
      throw new Error(response.message || "Gửi tin nhắn thất bại");
    }

    return response;
  } catch (error) {
    console.error("Error sending Facebook message:", error);
    throw error;
  }
};

export const getFacebookMessagesDetailWithMeta = async (
  params: GetFacebookMessagesParams
): Promise<GetFacebookMessagesDetailResult> => {
  const { conversationId, pageId, offset = 0, limit = 120, signal } = params;

  try {
    const response = await apiClient.get<FacebookMessagesApiResponse>(
      `/api/facebook/internal/conversations/${conversationId}/messages/detail`,
      {
        params: {
          pageId,
          offset,
          limit,
        },
        signal,
      }
    );

    const messages = response.data?.items || [];

    if (!Array.isArray(messages)) {
      console.error("Messages is not an array:", messages);
      return {
        messages: [],
        offset: response.data?.offset || 0,
        total: response.data?.total || 0,
        limit: response.data?.limit || 120,
      };
    }

    const transformedMessages = messages
      .map((msg, index) => {
        try {
          return transformFacebookMessage(msg);
        } catch (error) {
          console.error(
            `Error transforming message at index ${index}:`,
            error,
            msg
          );
          return null;
        }
      })
      .filter((msg): msg is Message => msg !== null);

    return {
      messages: transformedMessages,
      offset: response.data?.offset || 0,
      total: response.data?.total || 0,
      limit: response.data?.limit || 120,
    };
  } catch (error) {
    console.error("Error in getFacebookMessagesDetailWithMeta:", error);
    throw error;
  }
};

export interface GetConversationTagsParams {
  externalIds: string | string[];
  platform: "Facebook" | "Zalo" | "Shopee";
  pageId: string;
}

export interface ConversationTagResponse {
  code: number;
  data: {
    items:
      | Array<{
          total: number;
          externalId: string;
          tags: Array<{
            description: string;
            id: number;
            tagName: string;
          }>;
        }>
      | [];
  };
  message: string;
}

export const getConversationTags = async (
  params: GetConversationTagsParams
): Promise<Array<{ description: string; id: number; tagName: string }>> => {
  const { externalIds, platform, pageId } = params;

  try {
    const externalIdsArray = Array.isArray(externalIds)
      ? externalIds
      : [externalIds];

    const chunks = chunkArray(externalIdsArray, MAX_EXTERNAL_IDS_PER_REQUEST);
    
    const allTags: Array<{ description: string; id: number; tagName: string }> = [];
    const promises = chunks.map((chunk) =>
      apiClient.post<ConversationTagResponse>(
        "/api/conversation-tags/conversation",
        {
          externalIds: chunk,
          platform,
          pageId,
        }
      )
    );

    const responses = await Promise.all(promises);
    
    responses.forEach((response) => {
      if (response.data?.items && response.data.items.length > 0) {
        response.data.items.forEach((item) => {
          if (item.tags && item.tags.length > 0) {
            allTags.push(...item.tags);
          }
        });
      }
    });

    if (!Array.isArray(externalIds)) {
      return allTags.length > 0 ? allTags : [];
    }
    
    return allTags;
  } catch (error) {
    console.error("Error getting conversation tags:", error);
    return [];
  }
};

export const getConversationTagsBatch = async (
  params: GetConversationTagsParams
): Promise<
  Record<string, Array<{ description: string; id: number; tagName: string }>>
> => {
  const { externalIds, platform, pageId } = params;

  try {
    const externalIdsArray = Array.isArray(externalIds)
      ? externalIds
      : [externalIds];

    if (externalIdsArray.length === 0) {
      return {};
    }

    // Chunk externalIds thành các batch <= 100
    const chunks = chunkArray(externalIdsArray, MAX_EXTERNAL_IDS_PER_REQUEST);
    
    // Gọi API cho từng batch và merge kết quả
    const tagsMap: Record<
      string,
      Array<{ description: string; id: number; tagName: string }>
    > = {};

    const promises = chunks.map((chunk) =>
      apiClient.post<ConversationTagResponse>(
        "/api/conversation-tags/conversation",
        {
          externalIds: chunk,
          platform,
          pageId,
        }
      )
    );

    const responses = await Promise.all(promises);
    
    // Merge kết quả từ tất cả các batch
    responses.forEach((response) => {
      if (response.data?.items) {
        response.data.items.forEach((item) => {
          tagsMap[item.externalId] = item.tags || [];
        });
      }
    });

    externalIdsArray.forEach((externalId) => {
      if (!tagsMap[externalId]) {
        tagsMap[externalId] = [];
      }
    });

    return tagsMap;
  } catch (error) {
    console.error("Error getting conversation tags batch:", error);
    return {};
  }
};

export interface TagListItem {
  id: string;
  description: string;
  tagName: string;
}

export interface TagListResponse {
  code: number;
  data: {
    items?: TagListItem[];
    tags?: TagListItem[];
    total?: number;
  };
  message: string;
}

export interface GetTagListParams {
  offset?: number;
  limit?: number;
}

export const getTagList = async (
  params: GetTagListParams = { offset: 0, limit: 20 }
): Promise<TagListItem[]> => {
  const { offset = 0, limit = 20 } = params;

  try {
    const response = await apiClient.get<TagListResponse>(
      `/api/conversation-tags/list?offset=${offset}&limit=${limit}`
    );

    const responseData = response.data;
    if (responseData) {
      return responseData.items || responseData.tags || [];
    }
    return [];
  } catch (error) {
    console.error("Error getting tag list:", error);
    return [];
  }
};

export interface AssignTagParams {
  externalId: string;
  platform: "Facebook" | "Zalo" | "Shopee";
  tagId: number | string;
  pageId?: string;
}

export interface AssignTagsParams {
  externalId: string;
  platform: "Facebook" | "Zalo" | "Shopee";
  tagIds: (number | string)[];
  pageId?: string;
}

export interface UnassignTagsParams {
  externalId: string;
  platform: "Facebook" | "Zalo" | "Shopee";
  tagIds: (number | string)[];
  pageId?: string;
}

export interface AssignTagResponse {
  code: number;
  data?: unknown;
  message: string;
}

export const assignTagToConversation = async (
  params: AssignTagParams
): Promise<AssignTagResponse> => {
  const { externalId, platform, tagId, pageId } = params;

  try {
    const response = await apiClient.post<AssignTagResponse>(
      "/api/conversation-tags/assign",
      {
        externalId,
        platform,
        tagId: typeof tagId === "string" ? parseInt(tagId, 10) : tagId,
        pageId,
      }
    );

    return response;
  } catch (error) {
    console.error("Error assigning tag to conversation:", error);
    throw error;
  }
};

export interface RefreshMessageAttachmentsParams {
  messageId: string;
  pageId: string;
}

export interface RefreshMessageAttachmentsResponse {
  code: number;
  message: string;
  data?: {
    newUrl?: string;
    messageId?: string;
    pageId?: string;
    [key: string]: unknown;
  };
}

export const refreshFacebookMessageAttachments = async (
  params: RefreshMessageAttachmentsParams
): Promise<RefreshMessageAttachmentsResponse> => {
  const { messageId, pageId } = params;

  try {
    const response = await apiClient.post<RefreshMessageAttachmentsResponse>(
      `/api/facebook/internal/messages/${messageId}/refresh-attachments?pageId=${pageId}`,
      {}
    );

    return response;
  } catch (error) {
    console.error("Error refreshing message attachments:", error);
    throw error;
  }
};

export interface BackfillParams {
  pageId: string;
  startTimeMs: number;
  endTimeMs: number;
}

export interface BackfillResponse {
  code: number;
  message?: string;
  data?: {
    syncId?: string;
    [key: string]: unknown;
  };
}

export interface SyncStatusResponse {
  code: number;
  message?: string;
  data?: {
    syncId: string;
    status: "RUNNING" | "COMPLETED" | "FAILED";
    progressPercentage: number;
    itemsSuccess: number;
    itemsFailed: number;
    totalItems: number;
    currentItemId?: string;
    pageId: string;
    startTimeMs: number;
    endTimeMs: number;
    createdAtMs: number;
  };
}

export interface GetSyncStatusParams {
  syncId: string;
  pageId: string;
}

export interface GetActiveSyncParams {
  pageId: string;
}

export interface GetActiveSyncResponse {
  code: number;
  message?: string;
  data?: {
    pageId: string;
    syncId: string;
  };
}

export const getActiveSync = async (
  params: GetActiveSyncParams
): Promise<GetActiveSyncResponse> => {
  const { pageId } = params;

  try {
    const response = await apiClient.get<GetActiveSyncResponse>(
      `/api/facebook/internal/sync/active`,
      {
        params: {
          pageId,
        },
      }
    );
    return response;
  } catch (error) {
    console.error("Error getting active sync:", error);
    throw error;
  }
};

export const getSyncStatus = async (
  params: GetSyncStatusParams
): Promise<SyncStatusResponse> => {
  const { syncId, pageId } = params;

  try {
    const response = await apiClient.get<SyncStatusResponse>(
      `/api/facebook/internal/sync/${syncId}/status`,
      {
        params: {
          pageId,
        },
      }
    );
    return response;
  } catch (error) {
    console.error("Error getting sync status:", error);
    throw error;
  }
};

export const backfillFacebookConversations = async (
  params: BackfillParams
): Promise<BackfillResponse> => {
  const { pageId, startTimeMs, endTimeMs } = params;

  try {
    const response = await apiClient.post<BackfillResponse>(
      `/api/facebook/internal/backfill`,
      {},
      {
        params: {
          pageId,
          startTimeMs,
          endTimeMs,
        },
      }
    );
    return response;
  } catch (error) {
    console.error("Error backfilling Facebook conversations:", error);
    throw error;
  }
};

export const assignTagsToConversation = async (
  params: AssignTagsParams
): Promise<AssignTagResponse> => {
  const { externalId, platform, tagIds, pageId } = params;

  try {
    const response = await apiClient.post<AssignTagResponse>(
      "/api/conversation-tags/assign",
      {
        externalId,
        platform,
        tagIds: tagIds.map((tagId) =>
          typeof tagId === "string" ? parseInt(tagId, 10) : tagId
        ),
        pageId,
      }
    );

    return response;
  } catch (error) {
    console.error("Error assigning tags to conversation:", error);
    throw error;
  }
};

export const unassignTagsFromConversation = async (
  params: UnassignTagsParams
): Promise<AssignTagResponse> => {
  const { externalId, platform, tagIds, pageId } = params;

  try {
    const response = await apiClient.post<AssignTagResponse>(
      "/api/conversation-tags/unassign",
      {
        externalId,
        platform,
        tagIds: tagIds.map((tagId) =>
          typeof tagId === "string" ? parseInt(tagId, 10) : tagId
        ),
        pageId,
      }
    );

    return response;
  } catch (error) {
    console.error("Error unassigning tags from conversation:", error);
    throw error;
  }
};

export interface MessageTemplate {
  id: number;
  name: string;
  payload: string;
  scope: string;
  status: string;
  type: string;
}

export interface GetMessageTemplatesParams {
  name?: string;
  type?: string;
  pageId?: string;
  scope?: string;
  mine?: boolean;
  status?: string;
  offset?: number;
  limit?: number;
}

export interface GetMessageTemplatesResponse {
  code: number;
  message: string;
  data: {
    items: MessageTemplate[];
    limit: number;
    offset: number;
    total: number;
  };
}

export const getMessageTemplates = async (
  params: GetMessageTemplatesParams
): Promise<GetMessageTemplatesResponse> => {
  const {
    name,
    type,
    pageId,
    scope,
    mine,
    status,
    offset = 0,
    limit = 20,
  } = params;

  try {
    const requestParams: Record<string, unknown> = {
      offset,
      limit,
    };

    if (name) {
      requestParams.name = name;
    }

    if (type) {
      requestParams.type = type;
    }

    if (pageId) {
      requestParams.pageId = pageId;
    }

    if (scope) {
      requestParams.scope = scope;
    }

    if (mine !== undefined) {
      requestParams.mine = mine;
    }

    if (status) {
      requestParams.status = status;
    }

    const response = await apiClient.get<GetMessageTemplatesResponse>(
      "/api/facebook/message-template/list",
      {
        params: requestParams,
      }
    );

    return response;
  } catch (error) {
    console.error("Error getting message templates:", error);
    throw error;
  }
};

export interface CreateMessageTemplateParams {
  name: string;
  type: string;
  payload: string | Record<string, unknown>;
  scope?: string;
  status?: string;
}

export interface CreateMessageTemplateResponse {
  code: number;
  message: string;
  data?: MessageTemplate;
}

export const createMessageTemplate = async (
  params: CreateMessageTemplateParams
): Promise<CreateMessageTemplateResponse> => {
  try {
    const payload = typeof params.payload === 'string' 
      ? params.payload 
      : JSON.stringify(params.payload);

    const response = await apiClient.post<CreateMessageTemplateResponse>(
      "/api/facebook/message-template",
      {
        name: params.name,
        type: params.type,
        payload,
        scope: params.scope || "USER",
        status: params.status || "ACTIVE",
      }
    );

    return response;
  } catch (error) {
    console.error("Error creating message template:", error);
    throw error;
  }
};

export interface UpdateMessageTemplateParams {
  id: number;
  name?: string;
  type?: string;
  payload?: string | Record<string, unknown>;
  scope?: string;
  status?: string;
}

export interface UpdateMessageTemplateResponse {
  code: number;
  message: string;
  data?: MessageTemplate;
}

export const updateMessageTemplate = async (
  params: UpdateMessageTemplateParams
): Promise<UpdateMessageTemplateResponse> => {
  try {
    const body: Record<string, unknown> = {};

    if (params.name !== undefined) {
      body.name = params.name;
    }
    if (params.type !== undefined) {
      body.type = params.type;
    }
    if (params.payload !== undefined) {
      body.payload = typeof params.payload === 'string' 
        ? params.payload 
        : JSON.stringify(params.payload);
    }
    if (params.scope !== undefined) {
      body.scope = params.scope;
    }
    if (params.status !== undefined) {
      body.status = params.status;
    }

    const response = await apiClient.put<UpdateMessageTemplateResponse>(
      `/api/facebook/message-template/${params.id}`,
      body
    );

    return response;
  } catch (error) {
    console.error("Error updating message template:", error);
    throw error;
  }
};

export interface DeleteMessageTemplateResponse {
  code: number;
  message: string;
  data?: unknown;
}

export interface GetMessageTemplateByIdResponse {
  code: number;
  message: string;
  data?: MessageTemplate | { template: MessageTemplate };
}

export const getMessageTemplateById = async (
  id: number
): Promise<GetMessageTemplateByIdResponse> => {
  try {
    const response = await apiClient.get<GetMessageTemplateByIdResponse>(
      `/api/facebook/message-template/${id}`
    );
    return response;
  } catch (error) {
    console.error("Error getting message template by id:", error);
    throw error;
  }
};

export const deleteMessageTemplate = async (
  id: number
): Promise<DeleteMessageTemplateResponse> => {
  try {
    const response = await apiClient.delete<DeleteMessageTemplateResponse>(
      `/api/facebook/message-template/${id}`
    );

    return response;
  } catch (error) {
    console.error("Error deleting message template:", error);
    throw error;
  }
};

export interface SendMessageTemplateParams {
  templateId: number;
  recipientId: string;
  pageId: string;
}

export interface SendMessageTemplateResponse {
  code: number;
  message: string;
  data?: unknown;
}

export const sendMessageTemplate = async (
  params: SendMessageTemplateParams
): Promise<SendMessageTemplateResponse> => {
  try {
    const { templateId, recipientId, pageId } = params;
    const response = await apiClient.post<SendMessageTemplateResponse>(
      `/api/facebook/message-template/${templateId}/send`,
      {},
      {
        params: {
          recipientId,
          pageId,
        },
      }
    );

    return response;
  } catch (error) {
    console.error("Error sending message template:", error);
    throw error;
  }
};

export interface MarkConversationAsDoneParams {
  conversationId: string;
  pageId: string;
}

export interface MarkConversationAsDoneResponse {
  code: number;
  message: string;
  data?: unknown;
}

export const markConversationAsDone = async (
  params: MarkConversationAsDoneParams
): Promise<MarkConversationAsDoneResponse> => {
  const { conversationId, pageId } = params;

  try {
    const response = await apiClient.post<MarkConversationAsDoneResponse>(
      `/api/facebook/internal/conversations/${conversationId}/mark-done`,
      {},
      {
        params: {
          pageId,
        },
      }
    );

    return response;
  } catch (error) {
    console.error("Error marking conversation as done:", error);
    throw error;
  }
};

export interface UnmarkConversationAsDoneParams {
  conversationId: string;
  pageId: string;
}

export interface UnmarkConversationAsDoneResponse {
  code: number;
  message: string;
  data?: unknown;
}

export const unmarkConversationAsDone = async (
  params: UnmarkConversationAsDoneParams
): Promise<UnmarkConversationAsDoneResponse> => {
  const { conversationId, pageId } = params;

  try {
    const response = await apiClient.post<UnmarkConversationAsDoneResponse>(
      `/api/facebook/internal/conversations/${conversationId}/remove-done`,
      {},
      {
        params: {
          pageId,
        },
      }
    );

    return response;
  } catch (error) {
    console.error("Error unmarking conversation as done:", error);
    throw error;
  }
};

export interface CheckFolderStatusParams {
  pageId: string;
  externalIds: string[];
  folders: ("DONE" | "SPAM")[];
}

export interface CheckFolderStatusResponse {
  code: number;
  data?: {
    items: Array<{ folder?: string; externalId: string }>;
  };
  message?: string;
}

export const checkConversationFolderStatus = async (
  params: CheckFolderStatusParams
): Promise<CheckFolderStatusResponse> => {
  const { pageId, externalIds, folders } = params;

  try {
    const response = await apiClient.post<CheckFolderStatusResponse>(
      `/api/facebook/internal/conversations/check-folder-status`,
      {
        externalIds,
        folders,
      },
      {
        params: {
          pageId,
        },
      }
    );

    return response;
  } catch (error) {
    console.error("Error checking conversation folder status:", error);
    throw error;
  }
};

export interface PinConversationParams {
  conversationId: string;
  pageId: string;
}

export interface PinConversationResponse {
  code: number;
  message: string;
  data?: unknown;
}

export const pinConversation = async (
  params: PinConversationParams
): Promise<PinConversationResponse> => {
  const { conversationId, pageId } = params;

  try {
    const response = await apiClient.post<PinConversationResponse>(
      `/api/conversations/pin/${conversationId}/pin`,
      {},
      {
        params: {
          pageId,
        },
      }
    );

    return response;
  } catch (error) {
    console.error("Error pinning conversation:", error);
    throw error;
  }
};

export const unpinConversation = async (
  params: PinConversationParams
): Promise<PinConversationResponse> => {
  const { conversationId, pageId } = params;

  try {
    const response = await apiClient.post<PinConversationResponse>(
      `/api/conversations/pin/${conversationId}/unpin`,
      {},
      {
        params: {
          pageId,
        },
      }
    );

    return response;
  } catch (error) {
    console.error("Error unpinning conversation:", error);
    throw error;
  }
};
